import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomButton from "src/components/CustomButton";
import { GET_BANNERS, GET_BANNER_BY_ID } from "src/graphql/query";
import {
  Box,
  Checkbox,
  debounce,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  Tabs,
  Tab,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import ActionMenu from "../Business/ActionMenu";
import { getRole, hasPermissionPage } from "src/components/Common/Utils";
import CustomTable from "src/components/CustomTable";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteModel from "src/components/Common/DeleteModel";
import { DELETE_BANNER, UPDATE_BANNER } from "src/graphql/mutations";
import { toast } from "react-toastify";
import Loader from "src/components/Loader";
import ErrorComponent from "src/components/ErrorComponent";
import moment from "moment";
import BannerModal from "./BannerModal";
import BannerDetailsPage from "./BannerDetailsPage";

interface Banner {
  status: string;
  _id: string;
}


const Banner: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openDeleteMultipleModel, setOpenDeleteMultipleModel] =
    useState<boolean>(false);
  const [openDeleteAllModel, setOpenDeleteAllModel] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });
  const [total, setTotal] = useState(0);
  const [list, setList] = useState<Banner[]>([]);
  const [selectedBanner, setSelectedBanner] = useState<Banner | null>(
    null
  );
  const [isEditBanner, setIsEditBanner] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  const [deleteBanner] = useMutation(DELETE_BANNER);
  const isSuperAdmin = getRole() === "SuperAdmin";
  const initialBannerObj = {
    _id: "",
  };
  const [bannerObj, setBannerObj] = useState<any>(initialBannerObj);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [currentBanner, setCurrentBanner] = useState<any>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [updateBanner] = useMutation(UPDATE_BANNER);
  const [tabValue, setTabValue] = useState<"ACTIVE" | "INACTIVE">("ACTIVE");



  const COLUMNS = [
    {
      headerName: "Updated Date",
      field: "updatedAt",
      width: 120,
      renderCell: ({ row }: any) => {
        const date = new Date(row?.updatedAt);
        const day = String(date.getDate()).padStart(2, "0");
        const month = date.toLocaleString("en-US", { month: "short" }); // Apr
        const year = date.getFullYear();
        return <>{`${day}, ${month} ${year}`}</>;
      },
    },
    {
      headerName: "Title",
      field: "title",
      flex: 1,
      renderCell: ({ row }: any) => (
        <>
          <Box sx={{ cursor: "pointer" }} onClick={() => handleView(row)}>
            <Typography variant="body2" fontWeight={500}>
              {row?.title}
            </Typography>
          </Box>
        </>
      ),
    },

    {
      headerName: "Staus",
      field: "status",
      flex: 1,
      renderCell: ({ row }: any) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            style={{
              color: row?.status === "ACTIVE" ? "green" : "red",
              fontWeight: 600,
            }}
          >
            {row?.status?.charAt(0).toUpperCase() + row?.status?.slice(1).toLowerCase()}
          </Typography>
          <Tooltip title="Change Status">
            <IconButton
              size="small"
              onClick={() => {
                setCurrentBanner(row);
                setOpenStatusModal(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { headerName: "Section", field: "section", flex:1 },
    {
      headerName: "Start / End Date",
      field: "startDate",
      flex: 1,
      renderCell: ({ row }: { row: any }) => (
        <>
          <Typography variant="body2">
            <strong>SD:- </strong>{moment(row?.startDate).format("D, MMM YYYY")}
          </Typography>
          <Typography variant="body2">
            <strong>ED:- </strong>{moment(row?.endDate).format("D, MMM YYYY")}
          </Typography>
        </>
      ),
    },
    { headerName: "Description", field: "description", width: 250 },
    {
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }: { row: Banner }) => (
        <div>
          {hasPermissionPage("Banner", "update") && (
            <Tooltip title="Edit">
              <IconButton
                onClick={() => handleEdit(row)}
                aria-label="edit"
                style={{ marginRight: "8px", color: "#00C5B9" }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {hasPermissionPage("Banner", "delete") && (
            <Tooltip title="Delete">
              <IconButton
                onClick={() => handleDelete(row)}
                aria-label="delete"
                style={{ color: "#00C5B9" }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const { loading, error, data, refetch } = useQuery(GET_BANNERS, {
    variables: {
      page: pagination.page,
      limit: pagination.pageSize,
    },
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: "ACTIVE" | "INACTIVE") => {
    setTabValue(newValue);
  };

  const filteredList = list.filter((banner) => banner?.status === tabValue);

  

  useEffect(() => {
    if (data?.getBanners?.data) {
      const processedData = data?.getBanners?.data.map((banner: any) => ({
        ...banner,
      }));
      setList(processedData);
      setTotal(data?.getBanners?.count);
    }
  }, [data]);

  const debouncedRefetch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term.length > 0 ? term : "");
      refetch({
        search: term,
        page: pagination.page,
        limit: pagination.pageSize,
      });
    }, 1000),
    [refetch, pagination.page, pagination.pageSize]
  );

  const [getBanner] = useLazyQuery(GET_BANNER_BY_ID);

  const handleView = async (deal: { _id: string }) => {
    try {
      const response = await getBanner({ variables: { _id: deal._id } });
      setBannerObj(response.data?.getBanner?.data);
      setOpenViewModal(true);
    } catch (err) {
      console.error("Error fetching Banner data:", err);
    }
  };



  const handleEdit = async (rowData: any) => {
    try {
      if (!rowData?._id) {
        toast.error("Invalid row data");
        console.error("Invalid row data");
        return;
      }

      const res = await getBanner({
        variables: {
          _id: rowData._id,
        },
      });

      if (res?.data?.getBanner?.statusCode === 200) {
        const removeTypename = (obj: any): any => {
          if (Array.isArray(obj)) {
            return obj.map(removeTypename);
          } else if (typeof obj === "object" && obj !== null) {
            return Object.keys(obj).reduce((acc, key) => {
              if (key !== "__typename") {
                acc[key] = removeTypename(obj[key]);
              }
              return acc;
            }, {} as any);
          }
          return obj;
        };

        const bannerData = removeTypename(res?.data?.getBanner?.data);
        setBannerObj(bannerData);

        // const { __typename, ...Banner } = res?.data?.getCampaign.data;
        // setBannerObj(Banner);
        setOpenModal(true);
        setIsEditBanner(true);
      } else {
        toast.error("Failed to fetch Banner details");
        console.error("Failed to fetch Banner details");
      }
    } catch (error: any) {
      toast.error(error?.message);
      console.error(
        "An error occurred while fetching Banner details:",
        error
      );
    }
  };

  const handleDelete = (rowData: Banner) => {
    setSelectedBanner(rowData);
    setOpenConfirmDialog(true);
  };
  const confirmDelete = async () => {
    try {
      if (selectedBanner && selectedBanner._id) {
        const res = await deleteBanner({
          variables: {
            _id: selectedBanner._id,
          },
        });
        if (res?.data?.deleteBanner?.statusCode === 200) {
          toast.success(
            res?.data?.deleteBanner?.message ||
            "Banner deleted successfully!"
          );
          refetch();
        } else {
          throw new Error(res?.data?.deleteBanner?.message);
        }
      }
    } catch (error: any) {
      toast.error(
        error?.message || "An error occurred while deleting the Banner."
      );
    } finally {
      setOpenConfirmDialog(false);
      setSelectedBanner(null);
    }
  };
  const handleCloseModal = () => {
    setBannerObj(null);
    setOpenModal(false);
    setIsEditBanner(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
    debouncedRefetch(value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch({ search: "", page: 1, limit: pagination.pageSize });
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
    refetch({
      search: searchTerm,
      page: newPage + 1,
      limit: pagination.pageSize,
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    refetch({
      search: searchTerm,
      page: pagination.page,
      limit: newPageSize,
    });
  };

  const updateStatus = async () => {
    if (!currentBanner?._id) {
      toast.error("Invalid banner data");
      return;
    }

    setIsUpdatingStatus(true);

    try {
      const res = await updateBanner({
        variables: {
          _id: currentBanner._id,
          input: {
            status: currentBanner.status,
          }
        },
      });

      if (res?.data?.updateBanner?.statusCode === 200) {
        toast.success("Status updated successfully!");
        refetch();
      } else {
        throw new Error(res?.data?.updateBanner?.message || "Update failed");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to update status");
      console.error("Update status error:", error);
    } finally {
      setIsUpdatingStatus(false);
      setOpenStatusModal(false);
      setCurrentBanner(null);
    }
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  if (openViewModal) {
    return (
      <BannerDetailsPage
        bannerObj={bannerObj}
        handleClose={() => setOpenViewModal(false)}
        refetchBanner={function (): void {
          throw new Error("Function not implemented.");
        }} />
    );
  }

  return (
    <>
      <Box p={2}>
        {!openModal && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              paddingRight={4}
            >
              <Typography variant="h4" style={{ color: "#00C5B9" }}>
                Banners
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Box width={200}>
                  <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClearSearch}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {isSuperAdmin && (
                  <ActionMenu
                    isSuperAdmin={isSuperAdmin}
                    setOpenDeleteMultipleModel={setOpenDeleteMultipleModel}
                    setOpenDeleteAllModel={setOpenDeleteAllModel}
                  />
                )}
                {hasPermissionPage("Banner", "add") && (
                  <Box>
                    <CustomButton
                      // onClick={() => setOpenModal(true)}
                      onClick={() => {
                        setBannerObj(null);
                        setIsEditBanner(false);
                        setOpenModal(true);
                      }}
                      variant="contained"
                      className="width: 200px"
                    >
                      Create
                    </CustomButton>
                  </Box>
                )}
                <Box></Box>
              </Box>
            </Box>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label="Active" value="ACTIVE" />
              <Tab label="Inactive" value="INACTIVE" />
            </Tabs>

            <Box mt={2}>
              <CustomTable
                columns={COLUMNS}
                data={filteredList}
                paginationModel={{
                  page: pagination.page - 1,
                  pageSize: pagination.pageSize,
                }}
                totalCount={filteredList.length}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </Box>
          </>
        )}
        {openModal && (
          <BannerModal
            handleClose={handleCloseModal}
            refetchBanner={() => refetch()}
            initialBannerObj={initialBannerObj}
            bannerObj={bannerObj}
            setBannerObj={setBannerObj}
            isEditBanner={isEditBanner}
          />
        )}
        <DeleteModel
          open={openConfirmDialog}
          onClose={() => setOpenConfirmDialog(false)}
          onConfirm={confirmDelete}
          title="Confirm Deletion"
          message="Are you sure you want to delete this Banner?"
        />

        <Dialog open={openStatusModal} onClose={() => setOpenStatusModal(false)}>
          <DialogTitle>Change Status</DialogTitle>
          <DialogContent>
            <Select
              fullWidth
              value={currentBanner?.status || ""}
              onChange={(e) =>
                setCurrentBanner((prev: any) => ({
                  ...prev,
                  status: e.target.value,
                }))
              }
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenStatusModal(false)}>Cancel</Button>
            <Button
              onClick={updateStatus}
              variant="contained"
              disabled={isUpdatingStatus}
            >
              {isUpdatingStatus ? "Saving..." : "Save"}
            </Button>

          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default Banner;